import z from "zod";
import { CountryType } from "./CountryType";
import { DateOnly } from "./DateOnly";
import type { SkuType } from "./Sku";
import type { OperatorType, ProductType } from "./types";
import type { TFunction } from "i18next";

export const frequencies = [
	{ label: "Monthly", value: 30 },
	{ label: "Half-monthly", value: 15 },
	{ label: "Weekly", value: 7 }
] as const;
export type Frequency = (typeof frequencies)[number]["value"];
export const frequencySchema = z.coerce
	.number().optional()
	.refine((val) => [7, 15, 30].includes(val ?? -1), {
		message: "Please select frequency",
	})
	// Transform to the specific literal union type
	.transform((val): 7 | 15 | 30 => {
		if (val === 7) return 7;
		if (val === 15) return 15;
		if (val === 30) return 30;
		// This should never happen due to the refinement above
		throw new Error("Invalid value");
	});

export function createAutoTopupSchema(t: TFunction<"error">, countries: CountryType[]) {
	return z.object({
		recurringProfileId: z.number().optional(),
		recipientCountryCode: z.string({ "required_error": t("select_country") }).min(1),
		internationalCallingCode: z.string().optional(),
		mobileNumber: z.string({ "required_error": t("mobile_number_required") }),
		contactNumber: z.string().optional(),
		userCurrencyCode: z.string().optional(),
		skuId: z.number({ "message": t("select_denomination") }),
		frequency: frequencySchema,
		topupDay: z.number({ required_error: t("start_day") }),
		startDate: z.string(),
	}).superRefine((data, context) => {
		if (!/^\d+$/.test(data?.mobileNumber ?? "")) {
			context.addIssue({
				code: "custom",
				path: ["mobileNumber"],
				message: t("invalid_mobile_number"),
			})
			return;
		}
		if (data?.mobileNumber.startsWith("0")) {
			context.addIssue({
				code: "custom",
				path: ["mobileNumber"],
				message: t("invalid_mobile_number"),
			})
			return;
		}
		const country = countries.find(c => c.countryCode === data.recipientCountryCode);
		if (!country) return;
		if (!country.localPhoneNumberLength.includes(data.mobileNumber?.length ?? 0)) {
			context.addIssue({
				code: "custom",
				path: ["mobileNumber"],
				message: `Mobile number must be ${country.localPhoneNumberLength.join(" or ")} digits long.`,
			});
			return;
		}
		if (data.frequency) {
			if (!data.topupDay) {
				context.addIssue({
					code: "custom",
					path: ["topupDay"],
					message: t("select_topup_day"),
				});
			}
			if (data.frequency === 7 && (data.topupDay < 1 || data.topupDay > 7)) {
				context.addIssue({
					code: "custom",
					path: ["topupDay"],
					message: t("select_topup_day"),
				});
			} else if (data.frequency === 15 && (data.topupDay < 1 || data.topupDay > 15)) {
				context.addIssue({
					code: "custom",
					path: ["topupDay"],
					message: t("select_topup_day"),
				});
			} else if (data.frequency === 30 && (data.topupDay < 1 || data.topupDay > 31)) {
				context.addIssue({
					code: "custom",
					path: ["topupDay"],
					message: t("select_topup_day"),
				});
			}
		}
	})
}
function calculateNextTopupDate(frequency: Frequency, topupDay: number, fromDate: DateOnly) {
	if (frequency === 30) {
		if (topupDay === 31) {
			const next = fromDate.endOfMonth;
			if (next.isLessThenEqualTo(fromDate)) return fromDate.endOfMonth.addMonths(1);
			return next;
		} else {
			let next = new DateOnly(fromDate.year, fromDate.month, topupDay);
			if (next.isGreaterThen(fromDate.endOfMonth)) {
				next = fromDate.endOfMonth;
				if (next.isLessThenEqualTo(fromDate)) {
					return new DateOnly(fromDate.year, fromDate.month + 1, topupDay);
				}
				else return next;
			}
			if (next.isLessThenEqualTo(fromDate)) {
				return next.addMonths(1);
			}
			return next;
		}
	} else if (frequency === 15) {
		if (fromDate.day < 15) {
			let next = new DateOnly(fromDate.year, fromDate.month, topupDay);
			if (next.isGreaterThen(fromDate)) return next;
			next = new DateOnly(fromDate.year, fromDate.month, topupDay + 15)
			if (next.isLessThenEqualTo(fromDate)) return next;
			return fromDate.endOfMonth;
		} else {
			let next = new DateOnly(fromDate.year, fromDate.month, topupDay + 15);
			if (next.isGreaterThen(fromDate.endOfMonth)) {
				next = fromDate.endOfMonth;
			}
			if (next.isLessThenEqualTo(fromDate)) {
				return new DateOnly(fromDate.year, fromDate.month + 1, topupDay);
			} else {
				return next;
			}
		}
	} else if (frequency === 7) {
		const dayOfWeek: number = fromDate.dayOfWeek;
		let diff = (topupDay + 7 - dayOfWeek) % 7;
		if (diff === 0) diff += 7;
		return fromDate.addDays(diff);
	} else {
		throw new Error("Unreachable");
	}
}

export function calculateNextTwoTopupDates(frequency: Frequency, topupDay: number, holdingDays = 2) {
	const fromDate = DateOnly.today.addDays(holdingDays - 1);
	const firstDate = calculateNextTopupDate(frequency, topupDay, fromDate);
	const diff = firstDate.differenceInDays(fromDate);
	if (frequency === 30 && diff > 15) return [firstDate];
	else if (frequency === 15 && diff > 8) return [firstDate];
	else if (frequency === 7 && diff > 4) return [firstDate];
	const secondDate = calculateNextTopupDate(frequency, topupDay, firstDate);
	return [firstDate, secondDate];
}
export enum RecurringProfileStatus {
	Pending = 0,
	Approved = 1,
	Rejected = 2,
	PaymentAuthorized = 3,
	PaymentFailed = 4,
	RequireAction = 5,
	Deactivated = 6,
}

export type RecurringProfileType = {
	readonly recipientCountryCode: string;
	readonly mobileNumber: string;
	readonly amount?: number;
	readonly skuId: number;
	readonly frequency: 7 | 15 | 30;
	readonly topupDay: number;
	readonly startDate: string;
	readonly recurringProfileId: number;
	readonly internationalCallingCode?: string | undefined;
	readonly userCurrencyCode: string;
	readonly language: string,
	readonly userId: string,
	readonly paymentProvider: string,
	readonly contactNumber?: string,
	// readonly fingerprint: string,
	readonly sku: SkuType,
	readonly product: ProductType,
	readonly operator: OperatorType
	readonly nextPaymentDate: string,
	readonly status: RecurringProfileStatus,
}

export function humanizeDay2(topupDay: number) {
	const j = topupDay % 10;
	const k = topupDay % 100;
	const day = j === 1 && k !== 11 ?
		topupDay + 'st' : j === 2 && k !== 12 ?
			topupDay + 'nd' : j === 3 && k !== 13 ?
				topupDay + 'rd' : topupDay + 'th';
	return day + ' day';
}
export function humanizeDay(t: TFunction<"translation", undefined>, topupDay: number, freq: Frequency) {
	const weekDays = [
		{ value: 1, label: t("period.monday") },
		{ value: 2, label: t("period.tuesday") },
		{ value: 3, label: t("period.wednesday") },
		{ value: 4, label: t("period.thursday") },
		{ value: 5, label: t("period.friday") },
		{ value: 6, label: t("period.saturday") },
		{ value: 7, label: t("period.sunday") }
	];
	const j = topupDay % 10;
	const k = topupDay % 100;
	if (freq === 7) {
		return weekDays[topupDay - 1]?.label;
	}
	const day = j === 1 && k !== 11 ?
		topupDay + 'st' : j === 2 && k !== 12 ?
			topupDay + 'nd' : j === 3 && k !== 13 ?
				topupDay + 'rd' : topupDay + 'th';
	return day + ' day';
}
export const formatAutopupMessage = (t: TFunction<"translation", undefined>, frequency: Frequency, topupDay: number, startDate: string) => {
	const startDateFormated = DateOnly.parse(startDate).format();
	const frequencyString = frequency === 7 ? "week" : frequency === 15 ? "half month" : "month";
	const topupDayString = humanizeDay(t, topupDay, frequency);
	return t("auto-topup.message", { frequencyString, topupDayString, startDateFormated })
	// const message = `Schedule: Every ${freq} on ${topupDayString} starting ${startDateFormated}.`;
	// return message;
}
export const formatAutopupMiniMessage = (t: TFunction<"translation", undefined>, frequency: Frequency, topupDay: number) => {
	const frequencyString = frequency === 7 ? "week" : frequency === 15 ? "half month" : "month";
	const topupDayString = humanizeDay(t, topupDay, frequency);
	return t("auto-topup.mini_message", { frequencyString, topupDayString });
	// const message = `Every ${frequencyString} on ${topupDayString}`;
	// return message;
}